import { Slot } from '@radix-ui/react-slot'
import { Link } from '@tanstack/react-router'
import { ChevronRight } from 'lucide-react'
import * as React from 'react'

import { cn } from '@/utils'

interface BreadcrumbProps extends React.ComponentPropsWithoutRef<'nav'> {}

const Breadcrumb = React.forwardRef<HTMLElement, BreadcrumbProps>(
  ({ className, ...props }, ref) => {
    return (
      <nav
        ref={ref}
        aria-label="Breadcrumb"
        className={cn('flex items-center text-sm', className)}
        {...props}
      />
    )
  },
)
Breadcrumb.displayName = 'Breadcrumb'

interface BreadcrumbListProps extends React.ComponentPropsWithoutRef<'ol'> {}

const BreadcrumbList = React.forwardRef<HTMLOListElement, BreadcrumbListProps>(
  ({ className, ...props }, ref) => {
    return (
      <ol
        ref={ref}
        className={cn('flex items-center gap-1.5', className)}
        {...props}
      />
    )
  },
)
BreadcrumbList.displayName = 'BreadcrumbList'

interface BreadcrumbItemProps extends React.ComponentPropsWithoutRef<'li'> {}

const BreadcrumbItem = React.forwardRef<HTMLLIElement, BreadcrumbItemProps>(
  ({ className, ...props }, ref) => {
    return (
      <li
        ref={ref}
        className={cn('flex items-center gap-1.5', className)}
        {...props}
      />
    )
  },
)
BreadcrumbItem.displayName = 'BreadcrumbItem'

interface BreadcrumbLinkProps extends React.ComponentPropsWithoutRef<'a'> {
  asChild?: boolean
}

const BreadcrumbLink = React.forwardRef<HTMLAnchorElement, BreadcrumbLinkProps>(
  ({ asChild, className, ...props }, ref) => {
    const Comp = asChild ? Slot : 'a'
    return (
      <Comp
        ref={ref}
        className={cn(
          'text-muted-foreground hover:text-foreground transition-colors',
          className,
        )}
        {...props}
      />
    )
  },
)
BreadcrumbLink.displayName = 'BreadcrumbLink'

interface BreadcrumbPageProps extends React.ComponentPropsWithoutRef<'span'> {}

const BreadcrumbPage = React.forwardRef<HTMLSpanElement, BreadcrumbPageProps>(
  ({ className, ...props }, ref) => {
    return (
      <span
        ref={ref}
        aria-disabled="true"
        aria-current="page"
        className={cn('text-foreground font-medium inline-flex', className)}
        {...props}
      />
    )
  },
)
BreadcrumbPage.displayName = 'BreadcrumbPage'

interface BreadcrumbSeparatorProps
  extends React.ComponentPropsWithoutRef<'li'> {}

const BreadcrumbSeparator: React.FC<BreadcrumbSeparatorProps> = ({
  children,
  className,
  ...props
}) => {
  return (
    <li
      role="presentation"
      aria-hidden="true"
      className={cn('text-muted-foreground', className)}
      {...props}
    >
      {children ?? <ChevronRight className="h-3.5 w-3.5" />}
    </li>
  )
}
BreadcrumbSeparator.displayName = 'BreadcrumbSeparator'

interface Crumb {
  label: React.ReactNode
  to?: string
}

interface BreadcrumbTrailProps {
  crumbs?: (Crumb | false | null | undefined)[]
  page: React.ReactNode
}

const BreadcrumbTrail: React.FC<BreadcrumbTrailProps> = ({
  crumbs = [],
  page,
}) => {
  const links = crumbs.filter((crumb): crumb is Crumb => Boolean(crumb))

  return (
    <Breadcrumb>
      <BreadcrumbList>
        <BreadcrumbItem>
          <BreadcrumbLink asChild>
            <Link to="/">Home</Link>
          </BreadcrumbLink>
        </BreadcrumbItem>
        {links.map(({ label, to }) => (
          <React.Fragment key={to ?? 'pending'}>
            <BreadcrumbSeparator />
            <BreadcrumbItem>
              {to ? (
                <BreadcrumbLink asChild>
                  <Link to={to}>{label}</Link>
                </BreadcrumbLink>
              ) : (
                label
              )}
            </BreadcrumbItem>
          </React.Fragment>
        ))}
        <BreadcrumbSeparator />
        <BreadcrumbItem>
          <BreadcrumbPage>{page}</BreadcrumbPage>
        </BreadcrumbItem>
      </BreadcrumbList>
    </Breadcrumb>
  )
}
BreadcrumbTrail.displayName = 'BreadcrumbTrail'

export {
  Breadcrumb,
  BreadcrumbList,
  BreadcrumbItem,
  BreadcrumbLink,
  BreadcrumbPage,
  BreadcrumbSeparator,
  BreadcrumbTrail,
  type Crumb,
}
